import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Paper,
  CircularProgress
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { toast } from 'react-toastify';
import { api, useAuth } from '../services/AuthService';
import BrowserPrintService from '../services/BrowserPrintService';
import { useAutoPrint } from '../hooks/useAutoPrint';

function PrintStation() {
  const { user } = useAuth();
  const [lanchonete, setLanchonete] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadLanchonete = useCallback(async () => {
    if (!user?.lanchonete_id) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/lanchonetes/${user.lanchonete_id}`);
      setLanchonete(response.data);
    } catch (error) {
      console.error('Erro ao carregar lanchonete:', error);
      toast.error('Erro ao carregar configuracao da impressora');
    } finally {
      setLoading(false);
    }
  }, [user?.lanchonete_id]);

  useEffect(() => {
    loadLanchonete();
  }, [loadLanchonete]);

  const { connected, lastPrintedId } = useAutoPrint({
    lanchonete,
    enabled: Boolean(lanchonete?.auto_print),
    showToasts: true
  });

  const handlePrintTest = async () => {
    if (!lanchonete) return;
    const success = await BrowserPrintService.imprimirTeste(lanchonete);
    if (success) {
      toast.success('Teste enviado para a impressora');
    } else {
      toast.error('Falha no teste de impressao');
    }
  };

  if (loading) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!user?.lanchonete_id) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" p={3}>
        <Typography>Esta conta nao esta associada a uma lanchonete.</Typography>
      </Box>
    );
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="#111"
      color="#fff"
      p={3}
    >
      <Paper sx={{ p: 4, maxWidth: 520, width: '100%', textAlign: 'center' }}>
        <PrintIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Estacao de Impressao
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {lanchonete?.name || 'Lanchonete'}
        </Typography>

        <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap" my={3}>
          <Chip
            label={connected ? 'WebSocket conectado' : 'WebSocket desconectado'}
            color={connected ? 'success' : 'error'}
            size="small"
          />
          <Chip
            label={lanchonete?.auto_print ? 'Auto-print ligado' : 'Auto-print desligado'}
            color={lanchonete?.auto_print ? 'success' : 'warning'}
            size="small"
          />
        </Box>

        {!lanchonete?.auto_print && (
          <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
            Ative &quot;Impressao Automatica&quot; em Admin Lanchonete antes de usar esta tela.
          </Typography>
        )}

        <Typography variant="body1" sx={{ mb: 1 }}>
          Aguardando pedidos... (atualiza a cada 5 segundos)
        </Typography>
        {lastPrintedId && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ultimo pedido impresso: #{lastPrintedId}
          </Typography>
        )}

        <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintTest}>
          Testar impressora
        </Button>
      </Paper>
    </Box>
  );
}

export default PrintStation;
